import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Image, 
  Video, 
  Music, 
  FileText, 
  Download, 
  Play,
  X,
  Sparkles,
  Eye
} from "lucide-react";

const MediaGallery = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [selectedMedia, setSelectedMedia] = useState(null);

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        const response = await axios.get(`${API}/media?committee=mosque-dawah`);
        setMedia(response.data);
      } catch (error) {
        console.error("Error fetching media:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMedia();
  }, []);

  const filteredMedia = activeTab === "all" 
    ? media 
    : media.filter(m => m.media_type === activeTab);

  const handleDownload = async (mediaItem) => {
    try {
      const response = await axios.get(`${API}/media/${mediaItem.media_id}/download`);
      if (response.data.file_data) {
        const link = document.createElement('a');
        link.href = `data:${response.data.mime_type};base64,${response.data.file_data}`;
        link.download = response.data.file_name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error("Download error:", error);
    }
  };

  const getMediaIcon = (type) => {
    switch (type) {
      case "image": return <Image className="w-6 h-6" />;
      case "video": return <Video className="w-6 h-6" />;
      case "audio": return <Music className="w-6 h-6" />;
      case "document": return <FileText className="w-6 h-6" />;
      default: return <FileText className="w-6 h-6" />;
    }
  };

  const mediaCounts = {
    all: media.length,
    image: media.filter(m => m.media_type === "image").length,
    video: media.filter(m => m.media_type === "video").length,
    audio: media.filter(m => m.media_type === "audio").length,
    document: media.filter(m => m.media_type === "document").length
  };

  return (
    <div className="bg-stone-50">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900 via-primary-800 to-emerald-900"></div>
        <div className="pattern-overlay absolute inset-0"></div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white/90 text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              Mosque and Da'wah Collection
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
              Media Gallery
            </h1>
            <p className="text-xl text-primary-100 leading-relaxed">
              Explore our collection of Islamic content including images, videos, audio recordings, and documents from the Mosque and Da'wah committee.
            </p>
          </div>
        </div>
      </section>

      {/* Gallery Content */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          {/* Filter Tabs */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-10">
            <div>
              <p className="text-primary-600 font-semibold text-sm uppercase tracking-wider mb-1">Browse Collection</p>
              <h2 className="font-heading text-2xl font-bold text-gray-900">
                {filteredMedia.length} {activeTab === "all" ? "Items" : activeTab.charAt(0).toUpperCase() + activeTab.slice(1) + "s"}
              </h2>
            </div>
            
            <Tabs defaultValue="all" className="w-full sm:w-auto" onValueChange={setActiveTab}>
              <TabsList className="bg-stone-100 p-1 rounded-xl flex-wrap h-auto">
                <TabsTrigger 
                  value="all" 
                  className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2"
                  data-testid="filter-all"
                >
                  All ({mediaCounts.all})
                </TabsTrigger>
                <TabsTrigger 
                  value="image" 
                  className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2"
                  data-testid="filter-image"
                >
                  <Image className="w-4 h-4 mr-1" />
                  Images ({mediaCounts.image})
                </TabsTrigger>
                <TabsTrigger 
                  value="video" 
                  className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2"
                  data-testid="filter-video"
                >
                  <Video className="w-4 h-4 mr-1" />
                  Videos ({mediaCounts.video})
                </TabsTrigger>
                <TabsTrigger 
                  value="audio" 
                  className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2"
                  data-testid="filter-audio"
                >
                  <Music className="w-4 h-4 mr-1" />
                  Audio ({mediaCounts.audio})
                </TabsTrigger>
                <TabsTrigger 
                  value="document" 
                  className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2"
                  data-testid="filter-document"
                >
                  <FileText className="w-4 h-4 mr-1" />
                  Docs ({mediaCounts.document})
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Media Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <Card key={i} className="aspect-[4/3] rounded-2xl bg-gray-100 animate-pulse"></Card>
              ))}
            </div>
          ) : filteredMedia.length === 0 ? (
            <Card className="p-16 text-center bg-stone-50 border-dashed rounded-3xl">
              <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-6">
                {activeTab === "all" ? <Image className="w-10 h-10 text-gray-400" /> : getMediaIcon(activeTab)}
              </div>
              <h3 className="font-heading text-2xl font-bold text-gray-900 mb-2">No Media Found</h3>
              <p className="text-gray-500 max-w-md mx-auto">
                {activeTab === "all" 
                  ? "The media gallery is empty. Check back later for new content!" 
                  : `No ${activeTab} files available yet. Try a different category.`}
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredMedia.map((item) => (
                <Card 
                  key={item.media_id} 
                  className="group bento-card overflow-hidden cursor-pointer"
                  onClick={() => setSelectedMedia(item)}
                  data-testid={`media-item-${item.media_id}`}
                >
                  <div className="aspect-[4/3] bg-gray-100 relative overflow-hidden">
                    {item.media_type === "image" && (
                      <img 
                        src={item.file_url} 
                        alt={item.title} 
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" 
                      />
                    )}
                    {item.media_type === "video" && (
                      <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                        <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Play className="w-8 h-8 text-white ml-1" />
                        </div>
                      </div>
                    )}
                    {item.media_type === "audio" && (
                      <div className="w-full h-full bg-gradient-to-br from-primary-600 to-primary-800 flex items-center justify-center">
                        <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Music className="w-8 h-8 text-white" />
                        </div>
                      </div>
                    )}
                    {item.media_type === "document" && (
                      <div className="w-full h-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                        <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                          <FileText className="w-8 h-8 text-white" />
                        </div>
                      </div>
                    )}
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                        <span className="text-white text-sm font-medium flex items-center gap-1">
                          <Eye className="w-4 h-4" />
                          View
                        </span>
                        {(item.media_type === "audio" || item.media_type === "video") && (
                          <button 
                            onClick={(e) => { e.stopPropagation(); handleDownload(item); }}
                            className="p-2 rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 transition-colors"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Type Badge */}
                    <div className="absolute top-3 left-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-sm ${
                        item.media_type === 'image' ? 'bg-green-500/90 text-white' :
                        item.media_type === 'video' ? 'bg-purple-500/90 text-white' :
                        item.media_type === 'audio' ? 'bg-blue-500/90 text-white' :
                        'bg-amber-500/90 text-white'
                      }`}>
                        {item.media_type}
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 truncate group-hover:text-primary-900 transition-colors">
                      {item.title}
                    </h3>
                    {item.description && (
                      <p className="text-sm text-gray-500 truncate mt-1">{item.description}</p>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Media Modal */}
      {selectedMedia && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedMedia(null)}
        >
          <div 
            className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <Button 
                variant="ghost" 
                size="icon"
                className="absolute top-4 right-4 z-10 bg-black/50 text-white hover:bg-black/70 rounded-full"
                onClick={() => setSelectedMedia(null)}
              >
                <X className="w-5 h-5" />
              </Button>
              
              {/* Media Content */}
              <div className="aspect-video bg-gray-900 flex items-center justify-center">
                {selectedMedia.media_type === "image" && (
                  <img 
                    src={selectedMedia.file_url} 
                    alt={selectedMedia.title} 
                    className="max-w-full max-h-full object-contain" 
                  />
                )}
                {selectedMedia.media_type === "video" && (
                  <video 
                    src={selectedMedia.file_url} 
                    controls 
                    autoPlay
                    className="max-w-full max-h-full" 
                  />
                )}
                {selectedMedia.media_type === "audio" && (
                  <div className="text-center p-12">
                    <div className="w-24 h-24 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-6">
                      <Music className="w-12 h-12 text-white" />
                    </div>
                    <h3 className="text-white text-xl font-semibold mb-4">{selectedMedia.title}</h3>
                    <audio 
                      src={selectedMedia.file_url} 
                      controls 
                      autoPlay
                      className="w-full max-w-md mx-auto" 
                    />
                  </div>
                )}
                {selectedMedia.media_type === "document" && (
                  <div className="text-center p-12">
                    <div className="w-24 h-24 rounded-2xl bg-amber-500 flex items-center justify-center mx-auto mb-6">
                      <FileText className="w-12 h-12 text-white" />
                    </div>
                    <h3 className="text-white text-xl font-semibold mb-2">{selectedMedia.title}</h3>
                    <p className="text-gray-400 mb-6">{selectedMedia.file_name}</p>
                    <Button asChild className="bg-white text-gray-900 hover:bg-gray-100">
                      <a href={selectedMedia.file_url} target="_blank" rel="noopener noreferrer">
                        Open Document
                      </a>
                    </Button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Info */}
            <div className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="font-heading text-xl font-bold text-gray-900 mb-1">{selectedMedia.title}</h2>
                  {selectedMedia.description && (
                    <p className="text-gray-600">{selectedMedia.description}</p>
                  )}
                </div>
                {(selectedMedia.media_type === "audio" || selectedMedia.media_type === "video") && (
                  <Button 
                    onClick={() => handleDownload(selectedMedia)}
                    className="btn-primary"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaGallery;
